import React, { useMemo, useState } from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import { InlineError } from "../../lib/display";
import type { FilterBarItem } from "./Table";
import {
  type CustomFieldFilter,
  type CustomFieldFilterEntry,
  type HouseholdCustomFieldDefinition,
  isSetField,
  pruneCustomFieldFilter,
  summarizeCustomFieldFilter,
  type UsableType,
  usableDefinitions,
} from "./customFieldFilter";

// The "Custom fields" item of the filter bar and the "Filter by custom fields"
// modal (spec `doc-custom-field-filtering-01a11cc6e668eb59`, R15, R16, R18 to
// R29, section 5.4). The terms "usable definition", "set field", "text-type"
// and "failed definitions read" are the terms of that spec. The match and the
// summary are the helpers of `customFieldFilter.ts`.

/** The `key` of the item in `Table`'s `filterBarItems`. */
export const CUSTOM_FIELDS_FILTER_BAR_KEY = "custom-fields";

/** The result of `useQueryHouseholdCustomFields`. */
export type HouseholdCustomFieldsQuery = {
  isPending: boolean;
  isError: boolean;
  data: readonly HouseholdCustomFieldDefinition[] | undefined;
};

/**
 * The custom field filter of one table.
 *
 * - `filterBarItems`: for `Table`'s `filterBarItems`. Empty while the
 *   definitions read runs, or when it succeeds with no usable definition
 *   (R15, D39).
 * - `filter`: the filter to match the rows with. The saved filter without
 *   the fields and options that the definitions do not list (R33), and empty
 *   until the definitions read succeeds (R34, D36, D39).
 * - `definitions`: the usable definitions, for `matchesCustomFieldFilter` and
 *   `hiddenRowIds`. Empty until the definitions read succeeds.
 *
 * `onApply` gets the new filter on "Apply (n)" (R26), and an empty filter on
 * the ✕ of the item and on the mobile "Clear" (R20). The caller saves it, and
 * clears the hidden rows from the selection (R37).
 */
export function useCustomFieldsFilter({
  definitionsQuery,
  savedFilter,
  onApply,
}: {
  definitionsQuery: HouseholdCustomFieldsQuery;
  savedFilter: CustomFieldFilter | undefined;
  onApply: (filter: CustomFieldFilter) => void;
}): {
  filterBarItems: FilterBarItem[];
  filter: CustomFieldFilter;
  definitions: HouseholdCustomFieldDefinition[];
} {
  const { isError, data } = definitionsQuery;

  const definitions = useMemo(
    () => (isError || data === undefined ? [] : usableDefinitions(data)),
    [isError, data],
  );

  const filter = useMemo(
    () => pruneCustomFieldFilter(savedFilter, definitions),
    [savedFilter, definitions],
  );

  const filterBarItems = useMemo((): FilterBarItem[] => {
    if (!isError && definitions.length === 0) {
      return [];
    }
    return [
      {
        key: CUSTOM_FIELDS_FILTER_BAR_KEY,
        element: (
          <CustomFieldsFilterItem
            isError={isError}
            definitions={definitions}
            filter={filter}
            onApply={onApply}
          />
        ),
        isActive: filter.length > 0,
        onClear: () => onApply([]),
      },
    ];
  }, [isError, definitions, filter, onApply]);

  return { filterBarItems, filter, definitions };
}

/**
 * The "Custom fields" item. "Failed to load custom fields" after a failed
 * definitions read, with no modal (R16, D37). Otherwise "Any ✎" while no
 * field is set, or the summary, ✎ and ✕ (R18). The summary is cut with "…" at
 * the width of the item (D28).
 */
export function CustomFieldsFilterItem({
  isError,
  definitions,
  filter,
  onApply,
}: {
  isError: boolean;
  definitions: readonly HouseholdCustomFieldDefinition[];
  filter: CustomFieldFilter;
  onApply: (filter: CustomFieldFilter) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const summary = summarizeCustomFieldFilter(filter, definitions);

  return (
    <InputGroup size="sm" className="flex-nowrap">
      <InputGroup.Text>Custom fields</InputGroup.Text>
      {isError ? (
        <InputGroup.Text className="bg-transparent">
          <InlineError>Failed to load custom fields</InlineError>
        </InputGroup.Text>
      ) : (
        <>
          <Button
            variant="outline-secondary"
            className="d-flex align-items-center text-start"
            style={{ flex: "1 1 auto", width: "1%", minWidth: 0 }}
            title={summary === "" ? undefined : summary}
            onClick={() => setIsOpen(true)}
          >
            {summary === "" ? (
              "Any ✎"
            ) : (
              <>
                <span
                  className="text-truncate"
                  style={{ minWidth: 0 }}
                  data-testid="custom-fields-summary"
                >
                  {summary}
                </span>
                <span className="flex-shrink-0">&nbsp;✎</span>
              </>
            )}
          </Button>
          {summary !== "" && (
            <Button
              variant="outline-secondary"
              aria-label="Clear custom fields"
              title="Clear custom fields"
              onClick={() => onApply([])}
            >
              ✕
            </Button>
          )}
          {isOpen && (
            <CustomFieldsFilterModal
              definitions={definitions}
              filter={filter}
              onApply={(next) => {
                setIsOpen(false);
                onApply(next);
              }}
              onCancel={() => setIsOpen(false)}
            />
          )}
        </>
      )}
    </InputGroup>
  );
}

const byDisplayName = (
  a: HouseholdCustomFieldDefinition,
  b: HouseholdCustomFieldDefinition,
) => a.displayName.localeCompare(b.displayName);

/** An entry of the definition's type with no value. */
function emptyEntry(
  definition: HouseholdCustomFieldDefinition,
): CustomFieldFilterEntry {
  const fieldKey = definition.fieldKey;
  switch (definition.fieldType as UsableType) {
    case "single_select":
    case "multi_select":
      return {
        fieldKey,
        fieldType: definition.fieldType as "single_select" | "multi_select",
        options: [],
      };
    case "boolean":
      return { fieldKey, fieldType: "boolean", answers: [] };
    case "text":
    case "url":
      return {
        fieldKey,
        fieldType: definition.fieldType as "text" | "url",
        text: "",
      };
    case "number":
      return { fieldKey, fieldType: "number", min: null, max: null };
    case "date":
      return { fieldKey, fieldType: "date", from: null, to: null };
  }
}

/**
 * The "Filter by custom fields" modal. It lists one row for each usable
 * definition (R21): the set fields open at the top, then the other fields,
 * each A to Z (R22, D33). The values change only in the modal until
 * "Apply (n)" (R26). "Cancel" and the ✕ of the modal close it with no filter
 * change (R27), and "Clear all" removes each value in the modal (R28, D30).
 */
export function CustomFieldsFilterModal({
  definitions,
  filter,
  onApply,
  onCancel,
}: {
  definitions: readonly HouseholdCustomFieldDefinition[];
  filter: CustomFieldFilter;
  onApply: (filter: CustomFieldFilter) => void;
  onCancel: () => void;
}) {
  // The values in the modal, in the order in which the advisor set the
  // fields (D41). An entry leaves when its field has no value left.
  const [draft, setDraft] = useState<CustomFieldFilter>(() =>
    filter.filter(isSetField),
  );
  const [search, setSearch] = useState("");
  // The set fields of the applied filter, when the modal opened.
  const [setKeys] = useState(
    () => new Set(filter.filter(isSetField).map((entry) => entry.fieldKey)),
  );
  const [openKeys, setOpenKeys] = useState(() => new Set(setKeys));
  // "Clear all" remounts the controls, so that the number boxes drop the text
  // they hold.
  const [clearCount, setClearCount] = useState(0);

  const rows = useMemo(() => {
    const usable = usableDefinitions(definitions);
    return [
      ...usable.filter((d) => setKeys.has(d.fieldKey)).sort(byDisplayName),
      ...usable.filter((d) => !setKeys.has(d.fieldKey)).sort(byDisplayName),
    ];
  }, [definitions, setKeys]);

  const shownRows = useMemo(() => {
    const text = search.toLowerCase();
    return rows.filter((d) => d.displayName.toLowerCase().includes(text));
  }, [rows, search]);

  const setEntry = (entry: CustomFieldFilterEntry) =>
    setDraft((prev) => {
      const index = prev.findIndex((e) => e.fieldKey === entry.fieldKey);
      if (!isSetField(entry)) {
        return index === -1 ? prev : prev.filter((_, i) => i !== index);
      }
      return index === -1
        ? [...prev, entry]
        : prev.map((e, i) => (i === index ? entry : e));
    });

  const toggleRow = (fieldKey: string) =>
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(fieldKey)) {
        next.delete(fieldKey);
      } else {
        next.add(fieldKey);
      }
      return next;
    });

  const setCount = draft.filter(isSetField).length;

  return (
    <Modal show onHide={onCancel} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Filter by custom fields</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Control
          type="search"
          className="mb-2"
          placeholder="Find a field…"
          aria-label="Find a field…"
          value={search}
          onChange={(ev) => setSearch(ev.target.value)}
        />
        <div key={clearCount}>
          {shownRows.map((definition) => {
            const isOpen = openKeys.has(definition.fieldKey);
            const entry =
              draft.find(
                (e) =>
                  e.fieldKey === definition.fieldKey &&
                  e.fieldType === definition.fieldType,
              ) ?? emptyEntry(definition);
            return (
              <div
                key={definition.fieldKey}
                className="border rounded mb-2"
                data-testid="custom-field-row"
                data-field-key={definition.fieldKey}
              >
                <Button
                  variant="link"
                  className="w-100 text-start text-body text-decoration-none"
                  aria-expanded={isOpen}
                  onClick={() => toggleRow(definition.fieldKey)}
                >
                  {isOpen ? "▾" : "▸"} {definition.displayName}
                </Button>
                {isOpen && (
                  <div className="border-top px-3 py-2">
                    <FieldControl
                      definition={definition}
                      entry={entry}
                      onChange={setEntry}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button
          variant="link"
          className="ps-0"
          onClick={() => {
            setDraft([]);
            setClearCount((count) => count + 1);
          }}
        >
          Clear all
        </Button>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => onApply(draft.filter(isSetField))}
          >
            Apply ({setCount})
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

/** The control of one field, per the table of R24. */
function FieldControl({
  definition,
  entry,
  onChange,
}: {
  definition: HouseholdCustomFieldDefinition;
  entry: CustomFieldFilterEntry;
  onChange: (entry: CustomFieldFilterEntry) => void;
}) {
  const id = `custom-field-${definition.fieldKey}`;
  switch (entry.fieldType) {
    case "single_select":
    case "multi_select":
      // A checkbox for each select option, in the order of the definition.
      return (
        <>
          {(definition.selectOptions ?? []).map((option, index) => (
            <Form.Check
              key={option}
              type="checkbox"
              id={`${id}-${index}`}
              label={option}
              checked={entry.options.includes(option)}
              onChange={(ev) =>
                onChange({
                  ...entry,
                  options: ev.target.checked
                    ? [...entry.options, option]
                    : entry.options.filter((o) => o !== option),
                })
              }
            />
          ))}
        </>
      );
    case "boolean":
      // "Yes", then "No" (D34).
      return (
        <>
          {[true, false].map((answer) => (
            <Form.Check
              key={String(answer)}
              type="checkbox"
              id={`${id}-${answer ? "yes" : "no"}`}
              label={answer ? "Yes" : "No"}
              checked={entry.answers.includes(answer)}
              onChange={(ev) =>
                onChange({
                  ...entry,
                  answers: ev.target.checked
                    ? [...entry.answers, answer]
                    : entry.answers.filter((a) => a !== answer),
                })
              }
            />
          ))}
        </>
      );
    case "text":
    case "url":
      return (
        <Form.Control
          type="text"
          size="sm"
          placeholder="contains"
          aria-label="contains"
          value={entry.text}
          onChange={(ev) => onChange({ ...entry, text: ev.target.value })}
        />
      );
    case "number":
      return <NumberRangeControl entry={entry} onChange={onChange} />;
    case "date":
      return <DateRangeControl entry={entry} onChange={onChange} />;
  }
}

/** An empty or blank box sets no limit (D24). */
function parseBox(text: string): number | null {
  if (text.trim() === "") {
    return null;
  }
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** A minimum box and a maximum box (R24), with the error text of R29. */
function NumberRangeControl({
  entry,
  onChange,
}: {
  entry: Extract<CustomFieldFilterEntry, { fieldType: "number" }>;
  onChange: (entry: CustomFieldFilterEntry) => void;
}) {
  // The text of each box, so that a box keeps text that is not yet a number.
  const [minText, setMinText] = useState(entry.min?.toString() ?? "");
  const [maxText, setMaxText] = useState(entry.max?.toString() ?? "");
  const hasError =
    entry.min !== null && entry.max !== null && entry.min > entry.max;

  return (
    <>
      <div className="d-flex gap-2">
        <Form.Control
          type="number"
          size="sm"
          placeholder="Minimum"
          aria-label="Minimum"
          value={minText}
          onChange={(ev) => {
            setMinText(ev.target.value);
            onChange({ ...entry, min: parseBox(ev.target.value) });
          }}
        />
        <Form.Control
          type="number"
          size="sm"
          placeholder="Maximum"
          aria-label="Maximum"
          value={maxText}
          onChange={(ev) => {
            setMaxText(ev.target.value);
            onChange({ ...entry, max: parseBox(ev.target.value) });
          }}
        />
      </div>
      {hasError && (
        <Form.Text className="text-danger d-block">
          Minimum cannot be more than maximum
        </Form.Text>
      )}
    </>
  );
}

/**
 * A "from" date and a "to" date (R24), with the error text of
 * `DateRangeFilter` (`filters.tsx`) (R29). The dates are `YYYY-MM-DD`, as the
 * date input gives them, so they compare as text.
 */
function DateRangeControl({
  entry,
  onChange,
}: {
  entry: Extract<CustomFieldFilterEntry, { fieldType: "date" }>;
  onChange: (entry: CustomFieldFilterEntry) => void;
}) {
  const hasError =
    entry.from !== null && entry.to !== null && entry.from > entry.to;

  return (
    <>
      <div className="d-flex gap-2">
        <InputGroup size="sm">
          <InputGroup.Text>from</InputGroup.Text>
          <Form.Control
            type="date"
            aria-label="from"
            value={entry.from ?? ""}
            onChange={(ev) =>
              onChange({ ...entry, from: ev.target.value || null })
            }
          />
        </InputGroup>
        <InputGroup size="sm">
          <InputGroup.Text>to</InputGroup.Text>
          <Form.Control
            type="date"
            aria-label="to"
            value={entry.to ?? ""}
            onChange={(ev) =>
              onChange({ ...entry, to: ev.target.value || null })
            }
          />
        </InputGroup>
      </div>
      {hasError && (
        <Form.Text className="text-danger d-block">
          Start date cannot be later than end date
        </Form.Text>
      )}
    </>
  );
}
